import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import Input from "../../../../../Components/Common/Input/Input";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { autoFillClass, prefillProps } from "../../utils/prefilled";
import DateSelect from "../../../../../Components/Common/DateSelect/DateSelect";
import { PreconFormValues } from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";

interface Props {
  prefilledFields: Set<string>;
}

const Step3ExecutiveSummary = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();

  const af = (field: keyof PreconFormValues) =>
    autoFillClass(field, prefilledFields);
  const pf = (field: keyof PreconFormValues) =>
    prefillProps(field, prefilledFields);
  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  useEffect(() => {
    const hard = parseFloat(values.hardCostBudget.replace(/\D/g, "") || "0");
    const contingency = parseFloat(
      values.contingencyDisplay.replace(/[^0-9.]/g, "") || "0",
    );
    const sf = parseFloat(values.totalSf.replace(/[^0-9.]/g, "") || "0");
    if (sf > 0 && (hard > 0 || contingency > 0)) {
      setFieldValue("costPerSf", ((hard + contingency) / sf).toFixed(2), false);
    } else {
      setFieldValue("costPerSf", "", false);
    }
  }, [
    values.hardCostBudget,
    values.contingencyDisplay,
    values.totalSf,
    setFieldValue,
  ]);
  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Project Description & Executive Summary"
        subtitle="Pre-populated from the GC checklist Q&A responses and project record. Edit and expand as needed."
      />

      <SourceBanner
        icon="📝"
        title="Pre-filled from GC checklist responses"
        text="Square footage, budget, timeline, and project description pulled from GC's submitted Q&A."
      />

      <Card
        title="Project Description Bullet Points"
        badge="Editable"
        badgeType="edit"
      >
        <div className="field fieldInline">
          <label>
            Primary Description Points <span className="req">*</span>
          </label>
          <TextArea
            name="primaryDescriptionPoints"
            className={`ft textareaXL${hasErr("primaryDescriptionPoints") ? " error-input-red" : ""}${prefilledFields.has("primaryDescriptionPoints") ? "" : ""}`}
            value={values.primaryDescriptionPoints}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            touched={isTouched("primaryDescriptionPoints")}
            error={err("primaryDescriptionPoints")}
          />
        </div>

        <div className="fieldRow c3">
          <div className="field">
            <label>Total SF (Phase 1)</label>
            <Input
              name="totalSf"
              className={`${af("totalSf")}${hasErr("totalSf", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.totalSf}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("totalSf")}
            />
            <ErrorMessage
              value={values.totalSf}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("totalSf")}
              error={err("totalSf")}
            />
          </div>
          <div className="field">
            <label>Total Units (Phase 1)</label>
            <Input
              name="totalUnitsPhase1"
              className={`fi${hasErr("totalUnitsPhase1", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.totalUnitsPhase1}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.totalUnitsPhase1}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("totalUnitsPhase1")}
              error={err("totalUnitsPhase1")}
            />
          </div>
          <div className="field">
            <label>Total Units (Full Build)</label>
            <Input
              name="totalUnitsFull"
              className={`fi${hasErr("totalUnitsFull", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.totalUnitsFull}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.totalUnitsFull}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("totalUnitsFull")}
              error={err("totalUnitsFull")}
            />
          </div>
        </div>

        <div className="field fieldInline">
          <label>Additional Description / Scope Notes</label>
          <TextArea
            name="additionalDescription"
            className={`ft${hasErr("additionalDescription") ? " error-input-red" : ""}`}
            value={values.additionalDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            touched={isTouched("additionalDescription")}
            error={err("additionalDescription")}
          />
        </div>
      </Card>

      <Card
        title="Budget & Timeline Summary"
        badge="From GC Checklist"
        badgeType="auto"
      >
        <div className="fieldRow c2">
          <div className="field">
            <label>Total Budget (Hard + Soft)</label>
            <Input
              name="totalBudget"
              className={`${af("totalBudget")}${hasErr("totalBudget", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.totalBudget}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("totalBudget")}
            />
            <ErrorMessage
              value={values.totalBudget}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("totalBudget")}
              error={err("totalBudget")}
            />
          </div>
          <div className="field">
            <label>Hard Cost Budget</label>
            <div className="inputWithPrefix">
              <span className="prefixSymbol">$</span>
              <Input
                name="hardCostBudget"
                className={`${af("hardCostBudget")}${hasErr("hardCostBudget", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
                value={values.hardCostBudget}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  handleChange(e);
                }}
                onBlur={handleBlur}
                maxLength={CHAR_LIMIT.SHORT}
                {...pf("hardCostBudget")}
              />
            </div>
            <ErrorMessage
              value={values.hardCostBudget}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("hardCostBudget")}
              error={err("hardCostBudget")}
            />
          </div>
        </div>
        <div className="fieldRow c3">
          <div className="field">
            <label>Cost per SF (Hard + Contingency)</label>
            <Input
              name="costPerSf"
              className="fi autoFill"
              value={values.costPerSf}
              onChange={handleChange}
              onBlur={handleBlur}
              readOnly
            />
            <ErrorMessage
              value={values.costPerSf}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("costPerSf")}
              error={err("costPerSf")}
            />
          </div>
          <div className="field">
            <label>
              Mobilization Date <span className="req">*</span>
            </label>
            <DateSelect
              id="mobilizationDate"
              value={values.mobilizationDate}
              onChange={(val) => setFieldValue("mobilizationDate", val, true)}
              error={hasErr("mobilizationDate")}
              disabled={prefilledFields.has("mobilizationDate")}
            />
            <ErrorMessage
              value={values.mobilizationDate}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("mobilizationDate")}
              error={err("mobilizationDate")}
            />
          </div>
          <div className="field">
            <label>
              Est. Substantial Completion <span className="req">*</span>
            </label>
            <DateSelect
              id="estCompletion"
              value={values.estCompletion}
              onChange={(val) => setFieldValue("estCompletion", val, true)}
              error={hasErr("estCompletion")}
              disabled={prefilledFields.has("estCompletion")}
            />
            <ErrorMessage
              value={values.estCompletion}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("estCompletion")}
              error={err("estCompletion")}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step3ExecutiveSummary;
